import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';

const ProtectedRoute = ({ children, requiredRole = null, requiredPermission = null, enforcePermission = false }) => {
  const { isAuthenticated, isLoading, hasRole, hasPermission, isAdmin } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    // If the user explicitly logged out, don't carry the current URL forward.
    // The explicitLogout flag is set in AuthContext before the LOGOUT dispatch,
    // so ProtectedRoute always sees it during the logout re-render.
    const wasExplicitLogout = sessionStorage.getItem('explicitLogout');
    if (wasExplicitLogout) {
      return <Navigate to="/login" replace />;
    }
    // Session expired or first visit — remember the intended destination
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has required role
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900">Access Denied</h1>
          <p className="text-xl text-gray-600 mt-4">
            You don't have the required role to access this page.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Required: {Array.isArray(requiredRole) ? requiredRole.join(', ') : requiredRole}
          </p>
          <button
            onClick={() => window.history.back()}
            className="mt-6 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Permission checks are opt-in via `enforcePermission` since most existing
  // routes pass `requiredPermission` without their permission data being reliable yet.
  if (enforcePermission && requiredPermission && !hasPermission(requiredPermission) && !isAdmin()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900">Access Denied</h1>
          <p className="text-xl text-gray-600 mt-4">
            You don't have permission to access this page.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Required: {requiredPermission}
          </p>
          <button
            onClick={() => window.history.back()}
            className="mt-6 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;